import {
	useCreateManufacturerMutation,
	useGetAllLocalizationCountriesQuery,
} from '@codezo/graphql';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { VetterSelector } from '../../components/selectors/VetterSelector';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectPopover } from '../../components/ui/select-popover';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';

/* ════════════════════════ ADD MANUFACTURER MODAL ════════════════════════ */

export function AddManufacturerModal({
	open,
	onOpenChange,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}) {
	const [name, setName] = useState('');
	const [country, setCountry] = useState('');
	const [description, setDescription] = useState('');
	const [website, setWebsite] = useState('');
	const [vetterEntityId, setVetterEntityId] = useState('');
	const [isActive, setIsActive] = useState(true);

	const [createManufacturer, { loading }] = useCreateManufacturerMutation();

	const { data: countriesData } = useGetAllLocalizationCountriesQuery({
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const countryOptions = useMemo(
		() =>
			countriesData?.getAllLocalizationCountries?.map((c) => ({
				value: c.name,
				label: c.name,
			})) ?? [],
		[countriesData]
	);

	const resetForm = () => {
		setName('');
		setCountry('');
		setDescription('');
		setWebsite('');
		setVetterEntityId('');
		setIsActive(true);
	};

	const handleSubmit = async () => {
		if (!name.trim()) return;

		try {
			const { data } = await createManufacturer({
				variables: {
					input: {
						name: name.trim(),
						country: country.trim() || undefined,
						description: description.trim() || undefined,
						website: website.trim() || undefined,
						vetterEntityId: vetterEntityId || undefined,
						isActive,
					},
				},
			});

			if (data?.createManufacturer?.success) {
				toast.success('Manufacturer created successfully');
				resetForm();
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.createManufacturer?.message || 'Failed to create manufacturer');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create manufacturer');
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Add manufacturer</DialogTitle>
					<DialogDescription>
						Add a new production partner to your supply chain.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<Label htmlFor="mfr-name">
									Name <span className="text-destructive">*</span>
								</Label>
								<Input
									id="mfr-name"
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Shenzhen Hexin"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Country</Label>
								<SelectPopover
									value={country}
									onChange={setCountry}
									options={countryOptions}
									placeholder="Select country"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="mfr-description">Description</Label>
								<Textarea
									id="mfr-description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder="What does this manufacturer produce?"
									rows={3}
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="mfr-website">Website</Label>
								<Input
									id="mfr-website"
									value={website}
									onChange={(e) => setWebsite(e.target.value)}
									placeholder="https://example.com"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Vetter entity</Label>
								<VetterSelector
									value={vetterEntityId}
									onChange={setVetterEntityId}
									placeholder="Assign a vetter (optional)"
									clearable
									creatable
								/>
							</div>

							<div className="flex items-center justify-between rounded-lg border border-line-2 px-3 py-2.5">
								<div>
									<Label htmlFor="mfr-active" className="text-sm">
										Active
									</Label>
									<p className="text-sm text-muted-foreground">
										Set this manufacturer as active immediately
									</p>
								</div>
								<Switch
									id="mfr-active"
									checked={isActive}
									onCheckedChange={setIsActive}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!name.trim() || loading}>
							{loading ? 'Creating…' : 'Create manufacturer'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
